import { useState } from "react";
import { otherAssetsApi, propertiesApi, vehiclesApi } from "@/features/worksheets/api";
import { CollectionCard, type Column } from "@/features/worksheets/components/collection-card";
import { OtherAssetDialog } from "@/features/worksheets/components/dialogs/other-asset-dialog";
import { PropertyDialog } from "@/features/worksheets/components/dialogs/property-dialog";
import { VehicleDialog } from "@/features/worksheets/components/dialogs/vehicle-dialog";
import { useCollection } from "@/features/worksheets/hooks/use-collection";
import { formatMoney } from "@/features/worksheets/money";
import type { OtherAsset, Property, Vehicle } from "@/features/worksheets/types";

interface DialogState<T> {
  open: boolean;
  record: T | null;
}

const propertyColumns: Column<Property>[] = [
  { header: "Description", cell: (r) => r.description },
  { header: "Value", cell: (r) => formatMoney(r.currentValue) },
  { header: "Loan Balance", cell: (r) => formatMoney(r.loanBalance) },
  { header: "Monthly Pmt", cell: (r) => formatMoney(r.monthlyPmt) },
  { header: "Income", cell: (r) => (r.incomeGen ? "Yes" : "No") },
];

const vehicleColumns: Column<Vehicle>[] = [
  { header: "Description", cell: (r) => r.description },
  { header: "Year / Make / Model", cell: (r) => [r.year, r.make, r.model].filter(Boolean).join(" ") },
  { header: "Value", cell: (r) => formatMoney(r.currentValue) },
  { header: "Loan Balance", cell: (r) => formatMoney(r.loanBalance) },
  { header: "Own / Lease", cell: (r) => (r.leaseLoan === "LEASE" ? "Lease" : "Own") },
];

const otherAssetColumns: Column<OtherAsset>[] = [
  { header: "Description", cell: (r) => r.description },
  { header: "Title", cell: (r) => r.title },
  { header: "Value", cell: (r) => formatMoney(r.currentValue) },
  { header: "Loan Balance", cell: (r) => formatMoney(r.loanBalance) },
];

export function AssetsTab({ caseId }: { caseId: string }) {
  const properties = useCollection(caseId, propertiesApi.list);
  const vehicles = useCollection(caseId, vehiclesApi.list);
  const otherAssets = useCollection(caseId, otherAssetsApi.list);

  const [propertyDialog, setPropertyDialog] = useState<DialogState<Property>>({
    open: false,
    record: null,
  });
  const [vehicleDialog, setVehicleDialog] = useState<DialogState<Vehicle>>({
    open: false,
    record: null,
  });
  const [otherAssetDialog, setOtherAssetDialog] = useState<DialogState<OtherAsset>>({
    open: false,
    record: null,
  });

  return (
    <div className="grid gap-6">
      <CollectionCard
        title="Real estate"
        addLabel="Add Property"
        columns={propertyColumns}
        rows={properties.rows}
        loading={properties.loading}
        onAdd={() => setPropertyDialog({ open: true, record: null })}
        onEdit={(row) => setPropertyDialog({ open: true, record: row })}
        onDelete={(row) => propertiesApi.remove(caseId, row.id)}
      />
      <CollectionCard
        title="Vehicles"
        addLabel="Add Vehicle"
        columns={vehicleColumns}
        rows={vehicles.rows}
        loading={vehicles.loading}
        onAdd={() => setVehicleDialog({ open: true, record: null })}
        onEdit={(row) => setVehicleDialog({ open: true, record: row })}
        onDelete={(row) => vehiclesApi.remove(caseId, row.id)}
      />
      <CollectionCard
        title="Other assets"
        addLabel="Add Asset"
        columns={otherAssetColumns}
        rows={otherAssets.rows}
        loading={otherAssets.loading}
        onAdd={() => setOtherAssetDialog({ open: true, record: null })}
        onEdit={(row) => setOtherAssetDialog({ open: true, record: row })}
        onDelete={(row) => otherAssetsApi.remove(caseId, row.id)}
      />

      <PropertyDialog
        caseId={caseId}
        record={propertyDialog.record}
        open={propertyDialog.open}
        onOpenChange={(open) => setPropertyDialog((s) => ({ ...s, open }))}
        onSaved={properties.reload}
      />
      <VehicleDialog
        caseId={caseId}
        record={vehicleDialog.record}
        open={vehicleDialog.open}
        onOpenChange={(open) => setVehicleDialog((s) => ({ ...s, open }))}
        onSaved={vehicles.reload}
      />
      <OtherAssetDialog
        caseId={caseId}
        record={otherAssetDialog.record}
        open={otherAssetDialog.open}
        onOpenChange={(open) => setOtherAssetDialog((s) => ({ ...s, open }))}
        onSaved={otherAssets.reload}
      />
    </div>
  );
}
